import CancelIcon from '@mui/icons-material/Cancel';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { CircularProgress, ClickAwayListener, Divider, Fade, IconButton, InputAdornment, MenuItem, MenuList, Popper, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from 'use-debounce';
import { ReactComponent as InvestorsIcon } from "../../assets/icons/investors.svg";
import { UsersContext } from '../../contexts/UsersContext';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import useBulkDashboards from '../../hooks/useBulkDashboards';
import useDashboards from '../../hooks/useDashboards';
import { updateDashboardFunc } from '../../lib/helper';
import { scrollbarStyle } from '../../shared/dashboard';
import theme from '../../theme';
import { Dashboard, DealOwner, RefreshDataEnum } from '../../types/files';
import { collectionKey } from '../organisms/dashboards/DashboardsView';
import { FallbackLoading } from '../templates/loader';
import { DashboardsContext } from '../../contexts/DashboardsContext';
import useBackwardsCompatible from '../../hooks/useBackwardsCompatible';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 'max-content',
        minWidth: 200,
        maxWidth: 400,
        height: 'fit-content',
        marginTop: 16,
        padding: '12px 16px',
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['900'],
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    mainlabel: {
        fontWeight: '600',
        fontSize: '1rem',
        color: theme.colors.neutral['600'],
    },
    sublabel: {
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    menu: {
        width: '100%',
        padding: 4,
        "&.Mui-focusVisible": {
            background: 'transparent !important',
        },
        "&:hover": {
            background: 'transparent !important',
        }
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 24,
        height: 24,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            padding: '0 8px !important',
            borderRadius: 24,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    expandMoreIcon: {
        color: theme.colors.neutral['500'],
        width: '20px',
        height: '20px',
        transition: '0.25s ease-in-out ',
        '&:hover': {
            color: theme.colors.neutral['700'],
        },
    },
    expandButton: {
        minWidth: '20px',
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent'
        }
    },
}));

const DealOwnerMenu: React.FC<{
    anchorElement?: HTMLElement|null,
    onClose?: () => void,
}> = ({ anchorElement, onClose }) => {
    const classes = useStyles();
    const { users } = useContext(UsersContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { workspace } = useContext(WorkspaceContext);
    const { dashboardOwner } = useBackwardsCompatible();
    const { getBulkDashboards } = useBulkDashboards();

    const { filterDashboardList } = useDashboards();

    const [loading, setLoading] = useState<boolean>(false);
    const [userId, setUserId] = useState<string>('');
    const [selected, setSelected] = useState<DealOwner|null>(null);
    const [dashboardArray, setDashboardArray] = useState<Dashboard[]>([]);
    const [owners, setOwners] = useState<(DealOwner | null)[]>([]);
    const userEmailRef = useRef<TextFieldProps>(null);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const bulkDashboards = useMemo(() => getBulkDashboards(collectionKey), [getBulkDashboards]);

    const debouncedName = useDebouncedCallback((name) => {setUserId(name);}, 100, { maxWait: 200 });

    const dashboardOwners = useMemo(() => {
        const uniqueOwners = new Map<string, DealOwner>();

        dashboardArray.map((dashboard) => dashboardOwner(dashboard))
            .filter((owner) => !!owner).forEach((owner) => {
                const key = `${owner?.name?.toLowerCase() || ''}-${owner?.email?.toLowerCase() || ''}`;

                if (!uniqueOwners.has(key))
                    uniqueOwners.set(key, owner!);
            });

        return Array.from(uniqueOwners.values()).sort((a, b) => {
            const nameA = a.name?.toLowerCase() || '';
            const nameB = b.name?.toLowerCase() || '';
            const emailA = a.email?.toLowerCase() || '';
            const emailB = b.email?.toLowerCase() || '';

            if (nameA !== nameB)
                return nameA.localeCompare(nameB);

            return emailA.localeCompare(emailB);
        });
    // eslint-disable-next-line
    }, [dashboardArray]);

    const checkSelecting = useCallback((owner: DealOwner) => selected === owner, [selected]);

    const handleSelect = useCallback((selection: DealOwner|null) => {
        const updatedDashboards = bulkDashboards.map(({dashboard}) => ({
            ...dashboard,
            userId: selection?.email || '',
            dealOwner: selection,
            refreshData: {
                ...dashboard.refreshData,
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Title,
            },
        }));
        setSelected(selection);
        Promise.all(updatedDashboards.map(dashboard => updateDashboardFunc({...dashboard}))).then(() => {
            setDashboards((prev) => Array.from(new Map([...prev, ...updatedDashboards].map(dashboard => [dashboard.id, dashboard])).values()));
            onClose?.();
        });
    // eslint-disable-next-line
    }, [bulkDashboards, onClose]);

    const handleClearSearch = useCallback(() => {
        if (userEmailRef.current)
            userEmailRef.current.value = '';
        setUserId('');
    }, []);

    useEffect(() => {
        if (!workspace?.id) {
            setOwners(users.map((user) => ({
                name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
                email: user?.email,
            })).filter((owner) => !userId
                || owner?.name?.toLowerCase().includes(userId?.toLowerCase())
                || owner?.email?.toLowerCase().includes(userId?.toLowerCase())));
        } else {
            setOwners(dashboardOwners.filter((owner) => !userId
                || owner?.name?.toLowerCase().includes(userId?.toLowerCase())
                || owner?.email?.toLowerCase().includes(userId?.toLowerCase())
            ));
        }
    // eslint-disable-next-line
    }, [dashboardOwners, userId]);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            setDashboardArray(filterDashboardList('', workspace?.id));
            setLoading(false);
            clearTimeout(timerRef.current);
        }, 500);

        return () => {
            clearTimeout(timerRef.current);
        }
    }, [filterDashboardList, workspace?.id]);

    useEffect(() => {
        setLoading(!!timerRef.current && !dashboardArray.length);
    }, [dashboardArray.length, filterDashboardList]);

    return (<>
        <Popper open={!!anchorElement} anchorEl={anchorElement}
            placement="bottom-end" sx={{ zIndex: 1500 }}>
            <ClickAwayListener onClickAway={() => onClose?.()}>
                <Stack className={classes.card} alignItems="flex-start" justifyContent="flex-start" width="100%">
                    <TextField autoFocus
                        variant="outlined"
                        className={classes.inputField}
                        placeholder={'Search deal owner ...'}
                        onChange={(e) => debouncedName(e.target.value)}
                        inputRef={userEmailRef}
                        disabled={!!selected}
                        sx={{ width: '100%' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ color: theme.palette.primary.main }}>
                                    <InvestorsIcon height={24} width={24} fill={theme.palette.primary.main} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end" >
                                    {!!userId && (
                                        <Fade in timeout={1000}>
                                            <IconButton size="small"
                                                onClick={handleClearSearch}
                                                sx={{ color: theme.palette.warning.dark }}
                                                disableRipple>
                                                <CancelIcon sx={{ width: 24, height: 24, }} />
                                            </IconButton>
                                        </Fade>
                                    )}
                                </InputAdornment>
                    )}} />
                    <Divider sx={{ width: '100%' }} />
                    <MenuList sx={{ minWidth: 300 }}>
                        {loading ? (<FallbackLoading />) : (
                            <Stack direction="column" sx={{ ...scrollbarStyle, maxHeight: 246, overflowY: 'auto' }} >
                                {owners.map((owner, i) => (<>
                                    <MenuItem className={classes.menu}
                                        onClick={() => handleSelect(owner!)}
                                        disabled={!!selected && !checkSelecting(owner!)}
                                        key={'deal-owner-menu-273-' + i}>
                                        <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start" width="100%">
                                            <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                                            <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between" width="100%">
                                                <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                                    {!!owner?.name && (
                                                        <Typography className={classes.label}>
                                                            {owner?.name}
                                                        </Typography>
                                                    )}
                                                    {!!owner?.email && (
                                                        <Typography className={classNames(classes.label, classes.sublabel)}>
                                                            {owner?.email}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {checkSelecting(owner!) && (
                                                    <CircularProgress size="24px" sx={{ color: theme.palette.primary.main }} />
                                                )}
                                            </Stack>
                                        </Stack>
                                    </MenuItem>
                                    <Divider sx={{ width: '100%' }} />
                                </>))}
                            </Stack>
                        )}
                    </MenuList>
                </Stack>
            </ClickAwayListener>
        </Popper>
    </>);
}

export default DealOwnerMenu;